import { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../../App";
import { 
  Eye, MessageCircle, Gift, BarChart2, 
  Edit3, TreePine, ExternalLink, ArrowRight 
} from "lucide-react";

export default function CoupleOverview() {
  const { wedding } = useOutletContext();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (wedding?.wedding_id) {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `${API}/analytics/wedding/${wedding.wedding_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setAnalytics(response.data);
        } catch (error) {
          console.error("Failed to fetch analytics:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAnalytics();
  }, [wedding]);

  if (!wedding) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-wedding-ivory/60">No wedding assigned to your account.</p>
          <p className="text-wedding-ivory/40 text-sm mt-2">Contact admin to set up your wedding.</p>
        </div>
      </div>
    );
  }

  const stats = [
    { label: "Page Views", value: analytics?.page_views || 0, icon: Eye },
    { label: "Tree Messages", value: analytics?.tree_submissions || 0, icon: MessageCircle },
    { label: "Artifact Scans", value: analytics?.artifact_scans || 0, icon: Gift },
    { label: "Approved Messages", value: analytics?.approved_submissions || 0, icon: BarChart2 },
  ];

  const quickActions = [
    { label: "Edit Landing Page", path: "/couple/edit", icon: Edit3 },
    { label: "Manage Tree", path: "/couple/tree", icon: TreePine },
    { label: "View Artifacts", path: "/couple/artifacts", icon: Gift },
    { label: "View Analytics", path: "/couple/analytics", icon: BarChart2 },
  ];

  return (
    <div className="space-y-8" data-testid="couple-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">
            Welcome, {wedding.couple_name_1} & {wedding.couple_name_2}
          </h1>
          <p className="text-wedding-ivory/60 mt-1">
            Manage your wedding page and memories
          </p>
        </div>
        <a 
          href={`/${wedding.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary inline-flex items-center gap-2"
        >
          View Live Page <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <stat.icon className="w-5 h-5 text-wedding-gold mb-3" />
            <div className="stat-value">{loading ? "—" : stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="font-serif text-xl text-wedding-ivory mb-4">Quick Actions</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <motion.div
              key={action.path}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              <Link
                to={action.path}
                className="card-hover p-6 flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-full bg-wedding-gold/10 flex items-center justify-center group-hover:bg-wedding-gold/20 transition-colors">
                  <action.icon className="w-6 h-6 text-wedding-gold" />
                </div>
                <div className="flex-1">
                  <p className="text-wedding-ivory font-medium">{action.label}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-wedding-ivory/30 group-hover:text-wedding-gold group-hover:translate-x-1 transition-all" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Wedding Info Card */}
      <motion.div
        className="card-hover p-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <h2 className="font-serif text-xl text-wedding-ivory mb-6">Wedding Details</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-wedding-ivory/50 text-sm uppercase tracking-wider mb-1">Page URL</p>
            <p className="text-wedding-ivory">
              <code className="bg-wedding-subtle px-2 py-1 rounded text-wedding-gold">
                /{wedding.slug}
              </code>
            </p>
          </div>
          <div>
            <p className="text-wedding-ivory/50 text-sm uppercase tracking-wider mb-1">Wedding Date</p>
            <p className="text-wedding-ivory">{wedding.wedding_date || "Not set"}</p>
          </div>
          <div>
            <p className="text-wedding-ivory/50 text-sm uppercase tracking-wider mb-1">Status</p>
            <span className={`badge ${wedding.status === "active" ? "badge-active" : "badge-inactive"}`}>
              {wedding.status}
            </span>
          </div>
          <div>
            <p className="text-wedding-ivory/50 text-sm uppercase tracking-wider mb-1">Visibility</p>
            <span className={`badge ${wedding.visibility === "public" ? "badge-active" : "badge-pending"}`}>
              {wedding.visibility === "public" ? "Public" : "Passcode Protected"}
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
